/**
 * Net Exposure — Backend-UI parity for the list page.
 *
 * Live (Lab `net-exposure.js` / `net-exposure-new.js`): `getBooksForBackendPre`
 * then `getBooksForBackend` via CM-API `useNetExposureBooks`.
 * Detail Book → workstation; View Total → advance.
 *
 * `variant="betfair"` = Lab menu "Betfair Net Exposure" (`netExposureNew`).
 * Default grid reuses shared `EventBooksTable` (Ponytail — no duplicate event grid).
 */

import { useMemo, useState } from 'react'
import { Button, Icon, Kpi, PageHead } from '@w7/ui'
import { useBetfairListBooks } from '@/api/hooks/net-exposure/useBetfairListBooks'
import { useNetExposureBooks } from '@/api/hooks/net-exposure/useBooks'
import {
  bfListBetfairRunnerAmounts,
  bfListLocalRunnerAmounts,
  bfListRunnerHeaders,
  filterEventsWithBetfairBook,
  hasBetfairListBookData,
  type BfListRunnerAmounts,
} from '@/api/map/betfairListBook'
import type { BetfairBookResponse } from '@/api/map/workstation'
import { useViewerCurrency } from '@/api/hooks/shared/useViewerCurrency'
import { count, money, moneyCompact } from '@/lib/format'
import { EventBooksTable } from '@/features/net-exposure/EventBooksTable'
import {
  BOOK_TYPES,
  NE_SORTS,
  SPORTS,
  WORSE_THAN_STEPS,
  eventNames,
  filterEvents,
  summariseNetExposure,
  type BookType,
  type NeSort,
  type NetExposureEvent,
  type SportName,
} from './fixture'
import './net-exposure.css'

function toneClass(value: number): string {
  return value < 0 ? 'w7-ne-neg' : 'w7-ne-pos'
}

/** Lab list page: bare indianCurrency numbers — no ₹ / Rs. prefix. */
function Amt({ value }: { value: number }) {
  const currency = useViewerCurrency()
  return (
    <span className={`w7-ne-amt ${toneClass(value)}`}>
      {money(value, { round: true, symbol: false, currency })}
    </span>
  )
}

function RunnerAmountCells({ amounts }: { amounts: BfListRunnerAmounts }) {
  return (
    <>
      <td className="w7-ne-cell">
        <Amt value={amounts.one} />
      </td>
      <td className="w7-ne-cell w7-ne-cell-x">
        {amounts.x == null ? <span className="w7-ne-dash">-</span> : <Amt value={amounts.x} />}
      </td>
      <td className="w7-ne-cell">
        <Amt value={amounts.two} />
      </td>
    </>
  )
}

/**
 * Lab CM Betfair Net Exposure table (`net-exposure-new` + showBetfairBook):
 * event title · runner-name headers · Match Odds label · Bf-Book betfair/local.
 * No View Total / Detail Book / Net Exposure column.
 */
function BetfairEventBlock({
  event,
  betfairBook,
  betfairBookFailed = false,
}: {
  event: NetExposureEvent
  betfairBook?: BetfairBookResponse
  betfairBookFailed?: boolean
}) {
  const market = event.matchOdds
  const horses = market?.horse ?? []
  const headers = bfListRunnerHeaders(horses)
  const showBf = Boolean(market && hasBetfairListBookData(betfairBook))
  const betfairAmts = bfListBetfairRunnerAmounts(horses, betfairBook)
  const localAmts = bfListLocalRunnerAmounts(horses, betfairBook)

  return (
    <table className="w7-ne-table w7-ne-table-bf">
      <tbody>
        <tr className="w7-ne-head">
          <th className="w7-ne-event-title">{event._id}</th>
          <th className="w7-ne-col">{headers.one}</th>
          <th className="w7-ne-col">{headers.x}</th>
          <th className="w7-ne-col">{headers.two}</th>
        </tr>
        {market ? (
          <tr className="w7-ne-row w7-ne-mo-label">
            <td className="w7-ne-market" colSpan={4}>
              {market.marketName || 'Match Odds'}
            </td>
          </tr>
        ) : null}
        {showBf ? (
          <>
            <tr className="w7-ne-row w7-ne-bf-book">
              <td className="w7-ne-market">Bf-Book - betfair</td>
              <RunnerAmountCells amounts={betfairAmts} />
            </tr>
            <tr className="w7-ne-row w7-ne-bf-book">
              <td className="w7-ne-market">Bf-Book - local</td>
              <RunnerAmountCells amounts={localAmts} />
            </tr>
          </>
        ) : (
          <tr className="w7-ne-row w7-ne-bf-miss">
            <td colSpan={4} className="w7-ne-bf-miss-cell">
              {betfairBookFailed
                ? 'Bf-Book unavailable — getBetfairBook failed (check CM-API / Betfair).'
                : 'Bf-Book pending or empty — waiting on getBetfairBook.'}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

export type NetExposurePageVariant = 'default' | 'betfair'

export function NetExposurePage({
  variant = 'default',
}: {
  /** Lab "Betfair Net Exposure" (`netExposureNew`) vs "Net Exposure". */
  variant?: NetExposurePageVariant
}) {
  const isBetfair = variant === 'betfair'
  const [bookType, setBookType] = useState<BookType>('My PT')
  const [sport, setSport] = useState<SportName>('Cricket')
  const [eventName, setEventName] = useState<string>('')
  const [liveOnly, setLiveOnly] = useState(false)
  const [worseThan, setWorseThan] = useState<number | null>(null)
  const [sort, setSort] = useState<NeSort>('worst')

  const { events, isLoading, isError, error, isFetching, refetch } = useNetExposureBooks({
    selectedType: bookType,
    eventType: sport,
    eventname: eventName || 'All',
    scope: isBetfair ? 'betfair' : 'all',
  })

  const names = useMemo(() => eventNames(events), [events])
  const visible = useMemo(
    () => filterEvents(events, sport, eventName || null, liveOnly, worseThan, sort),
    [events, sport, eventName, liveOnly, worseThan, sort],
  )
  const betfairMarketIds = useMemo(
    () =>
      isBetfair
        ? visible.map((e) => e.matchOdds?.marketId).filter((id): id is string => Boolean(id))
        : [],
    [isBetfair, visible],
  )
  const bfBooks = useBetfairListBooks(betfairMarketIds, isBetfair && !isLoading)
  /** Betfair: hide until getBetfairBook settles — no empty “waiting” shells. */
  const displayEvents = useMemo(
    () =>
      isBetfair
        ? filterEventsWithBetfairBook(visible, bfBooks.byMarketId, bfBooks.failedIds)
        : visible,
    [isBetfair, visible, bfBooks.byMarketId, bfBooks.failedIds],
  )
  const listLoading = isLoading || (isBetfair && bfBooks.isPending)
  const totals = useMemo(() => summariseNetExposure(displayEvents), [displayEvents])
  const isFiltered =
    sport !== 'All' || Boolean(eventName) || liveOnly || worseThan != null

  const clearFilters = () => {
    setSport('All')
    setEventName('')
    setLiveOnly(false)
    setWorseThan(null)
  }

  const sortLabel = NE_SORTS.find((entry) => entry.id === sort)?.label ?? 'Worst case first'
  const currency = useViewerCurrency()
  // Lab list KPIs / chips: bare figures (no ₹); POINTS keep Pts when symbol on.
  const rupees = (n: number) => money(n, { currency, symbol: currency !== 'INR' })
  const rupeesRound = (n: number) =>
    money(n, { round: true, currency, symbol: currency !== 'INR' })
  const rupeesCompact = (n: number) =>
    currency === 'INR'
      ? moneyCompact(n, false)
      : money(n, { round: true, currency })

  return (
    <div className="w7-ne">
      <PageHead
        title={
          <span className="w7-ne-title">
            <Icon name="net-exposure" size={22} className="w7-ne-title-mark" />
            {isBetfair ? 'Betfair Net Exposure' : 'Net Exposure'}
          </span>
        }
        subtitle={
          isBetfair
            ? 'Lab Betfair Net Exposure — Bf-Book via getBetfairBook · My PT / Total Book.'
            : 'Open event books beneath you — worst case first, same maths as Exposure.'
        }
        actions={
          <Button
            variant="secondary"
            icon="refresh"
            busy={isFetching || bfBooks.isFetching}
            onClick={() => refetch()}
          >
            Refresh
          </Button>
        }
      />

      <div className="w7-ne-toolbar">
        <div className="w7-ne-bar">
          <div
            className="w7-ne-seg"
            role="group"
            aria-label="Book type"
            data-active={bookType === 'Total Book' ? 'total' : 'pt'}
          >
            <span className="w7-ne-seg-thumb" aria-hidden="true" />
            {BOOK_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className="w7-ne-seg-btn"
                aria-pressed={bookType === type}
                onClick={() => setBookType(type)}
              >
                {type}
              </button>
            ))}
          </div>

          <div className="w7-ne-chips" role="group" aria-label="Sport">
            {SPORTS.map((item) => (
              <button
                key={item.value}
                type="button"
                className="w7-ne-chip"
                aria-pressed={sport === item.value}
                onClick={() => {
                  setSport(item.value)
                  setEventName('')
                }}
              >
                {item.name}
              </button>
            ))}
            <button
              type="button"
              className="w7-ne-chip"
              aria-pressed={liveOnly}
              onClick={() => {
                const next = !liveOnly
                if (next && eventName) {
                  const selected = events.find((e) => e._id === eventName)
                  if (selected && !selected.inPlay) setEventName('')
                }
                setLiveOnly(next)
              }}
            >
              In play only
            </button>
          </div>

          <span className="w7-spacer" />

          {isFiltered ? (
            <Button
              variant="ghost"
              icon="close"
              onClick={clearFilters}
              title="Clears sport, event, live and worse-than. Book and sort stay."
            >
              Clear
            </Button>
          ) : null}
        </div>

        <div className="w7-ne-bar w7-ne-bar-controls">
          <select
            className="w7-ne-select w7-ne-select-event"
            value={eventName}
            onChange={(event) => setEventName(event.target.value)}
            aria-label="Select or search event"
          >
            <option value="">All events</option>
            {names
              .filter((name) => {
                const match = events.find((e) => e._id === name)
                if (!match) return false
                if (sport !== 'All' && match.sport !== sport) return false
                if (liveOnly && !match.inPlay) return false
                return true
              })
              .map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
          </select>

          <select
            className="w7-ne-select"
            value={worseThan === null ? '' : String(worseThan)}
            onChange={(event) =>
              setWorseThan(event.target.value ? Number(event.target.value) : null)
            }
            aria-label="Show only positions worse than"
          >
            <option value="">Any worst case</option>
            {WORSE_THAN_STEPS.map((step) => (
              <option key={step} value={String(step)}>
                Worse than {rupeesRound(step)}
              </option>
            ))}
          </select>

          <select
            className="w7-ne-select"
            value={sort}
            onChange={(event) => setSort(event.target.value as NeSort)}
            aria-label="Sort events by"
          >
            {NE_SORTS.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </div>

        <p className="w7-ne-filter-meta" aria-live="polite">
          {count(totals.events)} {totals.events === 1 ? 'event' : 'events'} ·{' '}
          {count(totals.markets)} {totals.markets === 1 ? 'market' : 'markets'} ·{' '}
          {count(totals.fancyLines)} fancy · {sortLabel}
          {worseThan != null ? ` · worse than ${rupeesRound(worseThan)}` : ''}
          {liveOnly ? ' · in play' : ''}
          {` · ${bookType}`}
        </p>
      </div>

      <div className={`w7-grid ${isBetfair ? 'w7-grid-4' : 'w7-grid-5'} w7-ne-kpis`}>
        <Kpi
          label="Worst case"
          value={rupeesCompact(totals.worstCase)}
          exact={rupees(totals.worstCase)}
          tone={totals.worstCase < 0 ? 'loss' : 'neutral'}
          accent
          meta={`Across ${count(totals.events)} ${totals.events === 1 ? 'event' : 'events'}`}
        />
        <Kpi
          label="Best case"
          value={rupeesCompact(totals.bestCase)}
          exact={rupees(totals.bestCase)}
          tone={totals.bestCase > 0 ? 'profit' : 'neutral'}
          meta="If every market lands the book's way"
        />
        <Kpi
          label="In play"
          value={rupeesCompact(totals.liveWorst)}
          exact={rupees(totals.liveWorst)}
          tone={totals.liveWorst < 0 ? 'loss' : 'neutral'}
          meta={`${count(totals.liveEvents)} of ${count(totals.events)} events are live`}
          onClick={() => setLiveOnly((value) => !value)}
        />
        <Kpi
          label="Markets"
          value={count(totals.markets)}
          meta={
            isBetfair
              ? 'Betfair Match Odds books in view'
              : `In ${count(totals.events)} ${totals.events === 1 ? 'event' : 'events'}`
          }
        />
        {isBetfair ? null : (
          <Kpi
            label="Fancy lines"
            value={count(totals.fancyLines)}
            meta="Session and premium fancy books in view"
          />
        )}
      </div>

      <div className="w7-ne-kpi-note">
        <p className="w7-card-sub">
          Worst case adds up the losing outcome of every market as though they all settled against
          the book at the same moment. Inside a single market the 1, X and 2 cells are alternatives —
          the row&rsquo;s worst is the smallest of them, never their sum.
        </p>
        <p className="w7-ne-basis-note" data-book-type={bookType}>
          Showing <strong>{bookType}</strong>
          {sport !== 'All' ? (
            <>
              {' '}
              · <strong>{sport}</strong>
            </>
          ) : null}
          {liveOnly ? (
            <>
              {' '}
              · <strong>In play only</strong>
            </>
          ) : null}
          {worseThan != null ? (
            <>
              {' '}
              · worse than <strong>{rupeesRound(worseThan)}</strong>
            </>
          ) : null}
        </p>
        {isFiltered ? (
          <p className="w7-card-sub w7-dimmer">
            These figures describe the {count(totals.events)}{' '}
            {totals.events === 1 ? 'event' : 'events'} in view, not your whole book.
          </p>
        ) : null}
      </div>

      {listLoading ? (
        <div className="w7-ne-empty">Loading open books…</div>
      ) : isError ? (
        <div className="w7-ne-empty">
          Could not load books
          {error instanceof Error && error.message ? `: ${error.message}` : ''}
        </div>
      ) : displayEvents.length === 0 ? (
        <div className="w7-ne-empty">
          {isBetfair && visible.length > 0
            ? 'No events with Bf-Book data'
            : isFiltered
              ? 'No events match these filters'
              : 'No Data Found'}
        </div>
      ) : (
        <div className="w7-ne-events">
          {displayEvents.map((event) =>
            isBetfair ? (
              <BetfairEventBlock
                key={event._id}
                event={event}
                betfairBook={
                  event.matchOdds
                    ? bfBooks.byMarketId.get(event.matchOdds.marketId)
                    : undefined
                }
                betfairBookFailed={Boolean(
                  event.matchOdds &&
                    bfBooks.failedIds.includes(event.matchOdds.marketId),
                )}
              />
            ) : (
              <EventBooksTable key={event._id} event={event} book={bookType} />
            ),
          )}
        </div>
      )}
    </div>
  )
}
